/**
 * OWA-998: the browser half of the stale-save contract (server: server/src/lib/staleSave.ts).
 *
 * Every save of a stale-checked entity (task today) names `baseVersion`: the accepted version
 * the edit began from, as {addedOn, dbAddedOn}, or NO_BASE_VERSION for a new record. The server
 * refuses a save whose base is no longer current with a 409 STALE conflict carrying the record
 * as it is now and the version the edit began from.
 *
 * sameContent is the same comparison the server uses to recognize a retry that already went
 * through; the browser uses it to retire a refused save that changed nothing (plan step 16).
 */

import Base from '../entities/base';
import type {SchemaField} from '../@types/schema';

/** Identifies one accepted version of a record. Both are microsecond timestamps. */
export type VersionRef = {addedOn: number, dbAddedOn: number};

/** The base a save sends. */
export type BaseVersion = VersionRef | {addedOn: null, dbAddedOn: null};

/** The base of a new record: no accepted version. An object, because the entity constructors turn a null json field into {}. */
export const NO_BASE_VERSION: Readonly<{addedOn: null, dbAddedOn: null}> = Object.freeze({addedOn: null, dbAddedOn: null});

export type SaveBase =
    | {kind: 'absent'}
    | {kind: 'none'}
    | {kind: 'version', ref: VersionRef}
    | {kind: 'malformed'};

export function readBaseVersion(value: unknown): SaveBase {
    if (value === null || value === undefined || typeof value !== 'object') return {kind: 'absent'};
    const v = value as Record<string, unknown>;
    if (!('addedOn' in v) && !('dbAddedOn' in v)) return {kind: 'absent'};
    if (v.addedOn === null && v.dbAddedOn === null) return {kind: 'none'};
    if (typeof v.addedOn !== 'number' || typeof v.dbAddedOn !== 'number' ||
        !Number.isFinite(v.addedOn) || !Number.isFinite(v.dbAddedOn)) return {kind: 'malformed'};
    return {kind: 'version', ref: {addedOn: v.addedOn, dbAddedOn: v.dbAddedOn}};
}

type Versioned = {addedOn: number, dbAddedOn?: number | null};

export const versionRef = (row: Versioned): VersionRef =>
    ({addedOn: Number(row.addedOn), dbAddedOn: Number(row.dbAddedOn)});

export const sameVersion = (row: Versioned, ref: VersionRef): boolean =>
    Number(row.addedOn) === ref.addedOn && Number(row.dbAddedOn) === ref.dbAddedOn;

/** The 409 body of a refused stale save (single save) or the fatalError of a refused batch slot. */
export type StaleConflict = {code: 'STALE', message: string, current: unknown, base: unknown};

export const isStaleConflict = (value: unknown): value is StaleConflict =>
    !!value && typeof value === 'object' && (value as {code?: unknown}).code === 'STALE';

/**
 * OWA-842: the 409 body of a refused reassignment of a task whose timer has started (single save),
 * or the fatalError of such a refused batch slot. `current` is the task as the server holds it.
 */
export type AssigneeLocked = {code: 'ASSIGNEE_LOCKED', message: string, current: unknown};

export const isAssigneeLocked = (value: unknown): value is AssigneeLocked =>
    !!value && typeof value === 'object' && (value as {code?: unknown}).code === 'ASSIGNEE_LOCKED';

// ---------------------------------------------------------------------------------------
// Same editable content and existence (plan step 13). Mirrors the server's sameContent.
// ---------------------------------------------------------------------------------------

type ContentField = [name: string, type: string];

const contentFieldCache = new Map<Function, ContentField[]>();

/** The entity's own persisted fields; base fields (identity, versions, provenance, status) and transient fields are left out. */
function contentFields(entity: Base): ContentField[] {
    const ctor = entity.constructor as typeof Base;
    let fields = contentFieldCache.get(ctor);
    if (!fields) {
        const baseNames = new Set(Object.keys(Base.getSchema().fields));
        fields = Object.entries(ctor.getSchema().fields as Record<string, SchemaField>)
            .filter(([name, f]) => !baseNames.has(name) && f.transient !== true)
            .map(([name, f]) => [name, f.type]);
        contentFieldCache.set(ctor, fields);
    }
    return fields;
}

const toMillis = (value: unknown): unknown => {
    const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(String(value));
    return Number.isFinite(ms) ? ms : String(value);
};

/** JSON with object keys sorted, so key order never makes two equal values differ. */
const stableJson = (value: unknown): string => JSON.stringify(value, (_key, v) =>
    v && typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date)
        ? Object.fromEntries(Object.keys(v).sort().map(k => [k, (v as Record<string, unknown>)[k]]))
        : v);

/** A field's value in the form the database stores it. */
function canonical(type: string, value: unknown): unknown {
    if (type === 'boolean') return !!value;
    if (value === undefined || value === null || value === '') return null;
    if (type.endsWith('[]')) return Array.isArray(value) && value.length === 0 ? null : stableJson(value);
    // The entity constructors turn a missing json field into {}, so {} and null are the same
    if (type.startsWith('json')) return typeof value === 'object' && !Array.isArray(value) && Object.keys(value as object).length === 0 ? null : stableJson(value);
    switch (type) {
        case 'timestamp':
        case 'date':
            return toMillis(value);
        case 'bigint':
            // PostgreSQL and the entity constructor both round to the nearest integer
            return Math.round(Number(value));
        case 'float':
        case 'int':
        case 'integer':
        case 'number':
        case 'money':
            return Number(value);
        default:
            return value;
    }
}

/** money is NUMERIC(18,2): a sent value and its stored copy differ by at most half a cent. */
const MONEY_TOLERANCE = 0.005 + 1e-9;

const sameField = (a: Base, b: Base, [name, type]: ContentField): boolean => {
    const x = canonical(type, (a as unknown as Record<string, unknown>)[name]);
    const y = canonical(type, (b as unknown as Record<string, unknown>)[name]);
    if (type === 'money' && typeof x === 'number' && typeof y === 'number') return Math.abs(x - y) <= MONEY_TOLERANCE;
    return x === y;
};

/** Same editable content and existence. An active row (C or U) and a deleted row (D) are never the same. */
export function sameContent(a: Base, b: Base): boolean {
    if ((a.status === 'D') !== (b.status === 'D')) return false;
    return contentFields(a).every(field => sameField(a, b, field));
}

/** OWA-1433: the editable content fields whose stored form differs between two copies of one record. */
export function changedContentFields(a: Base, b: Base): string[] {
    return contentFields(a).filter(field => !sameField(a, b, field)).map(([name]) => name);
}
